import { useMemo } from 'react';
import type { FC } from 'react';
import { theme } from './Styles/Theme';
import { OktaAuth } from '@okta/okta-auth-js';
import { ThemeProvider } from '@emotion/react';
import {
	Security,
} from '@okta/okta-react';
import {
	doNothing,
	RollbarProvider,
} from '@fctg-corporate/smartscript-core';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Callback from './pages/Callback';
import AuthGate from './components/AuthGate';
import Home from './pages/Home';
import Page from './pages/Page';
import type { OktaClient, SmartScriptOktaConfig } from './types';

type AppProps = {
	smartScriptOktaConfigs: SmartScriptOktaConfig[]
};

const App: FC<AppProps> = ({ smartScriptOktaConfigs }) => {
	const oktaClients: OktaClient[] = useMemo(() =>
		smartScriptOktaConfigs.map(({ tokenName, path, oktaConfig }) => ({
			oktaAuth: new OktaAuth(oktaConfig),
			tokenName,
			path,
			scopes: oktaConfig.scopes,
		})),
	[smartScriptOktaConfigs]);

	const pages = oktaClients.map(({ oktaAuth, tokenName, path, scopes }) => {
		const PageElement = <Page tokenName={tokenName} oktaAuth={oktaAuth} path={path} scopes={scopes}/>;
		const CallbackElement = <Callback tokenName={tokenName} oktaAuth={oktaAuth} path={path} />;

		const RouteElement =
			<Security oktaAuth={oktaAuth} restoreOriginalUri={doNothing}>
				<Routes>
					<Route path="/" element={PageElement} />
					<Route path="/callback" element={CallbackElement} />
				</Routes>
			</Security>;

		return <Route key={path} path={`${path}/*`} element={RouteElement} />;
	});

	const AuthGateElement =
		<AuthGate oktaClients={oktaClients}>
			<Home />
		</AuthGate>;

	return (
		<RollbarProvider
			accessToken={import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN ?? ''}
			codeVersion={import.meta.env.VITE_COMMIT_SHORT_SHA ?? ''}
			config={{ enabled: import.meta.env.PROD }}
		>
			<ThemeProvider theme={theme}>
				<BrowserRouter>
					<Routes>
						<Route path="/" element={AuthGateElement} />
						{pages}
					</Routes>
				</BrowserRouter>
			</ThemeProvider>
		</RollbarProvider>
	);
};

export default App;
